import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch } from "../services/api";
import { AppShell, TopNav, BodyGrid, PageBody } from "../components/Layout";

const IDEA_PLACEHOLDER =
  `e.g. A subscription box for rare teas sourced directly from small farms in Taiwan and Japan. ` +
  `Targeting tea enthusiasts aged 28–45 who want discovery and ritual over convenience.`;

export default function NewBusinessPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [idea, setIdea]       = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState("");

  const wordCount = idea.trim().split(/\s+/).filter(Boolean).length;
  const canSubmit = wordCount >= 10 && !loading;

  async function handleSubmit() {
    if (!canSubmit) return;
    setLoading(true);
    setError("");
    try {
      const ws = await apiFetch("/api/workspaces", {
        method: "POST",
        body: JSON.stringify({ mode: "new", idea_description: idea.trim() }),
      });
      navigate(`/onboarding/stream?workspace_id=${ws.id}&mode=new&auto_run=false`);
    } catch (e) {
      setError(e.message || "Something went wrong. Please try again.");
      setLoading(false);
    }
  }

  return (
    <AppShell>
      <TopNav />
      <BodyGrid>
        <PageBody>

          <div style={s.pageHeader}>
            <button style={s.backLink} onClick={() => navigate("/onboarding")}>
              ← Back
            </button>
            <h1 style={s.pageTitle}>Describe your idea</h1>
            <p style={s.pageSubtitle}>
              Include what the business does, who it's for, and what makes it different.
              The more context you give, the better the analysis.
            </p>
          </div>

          <div style={s.card}>
            <div style={s.field}>
              <label style={s.label}>
                Your business idea <span style={s.required}>*</span>
              </label>
              <textarea
                style={{ ...s.textarea, ...(idea.length > 0 && wordCount < 10 ? s.textareaWarn : {}) }}
                placeholder={IDEA_PLACEHOLDER}
                value={idea}
                onChange={e => setIdea(e.target.value)}
                rows={7}
                autoFocus
              />
              <div style={s.textareaFooter}>
                <span style={{ ...s.wordCount, color: wordCount < 10 && idea.length > 0 ? "var(--accent)" : "var(--text-mute)" }}>
                  {wordCount < 10
                    ? `${10 - wordCount} more word${10 - wordCount !== 1 ? "s" : ""} needed`
                    : `${wordCount} words`}
                </span>
              </div>
            </div>

            {error && <p style={s.error}>{error}</p>}

            <div style={s.actions}>
              <button
                style={{ ...s.submitBtn, ...(!canSubmit ? s.submitBtnDisabled : {}) }}
                onClick={handleSubmit}
                disabled={!canSubmit}
              >
                {loading
                  ? <><span style={s.spinner} /> Analyzing…</>
                  : "Analyze my idea →"}
              </button>
              <p style={s.submitNote}>
                Your CEO agent will read this and generate a strategy, tasks, and team brief.
                Takes about 30–60 seconds.
              </p>
            </div>
          </div>

        </PageBody>
      </BodyGrid>
    </AppShell>
  );
}

/* ─────────────────────────────────────────────
   STYLES
   ───────────────────────────────────────────── */
const s = {
  pageHeader: {
    marginBottom: 24,
  },
  backLink: {
    background: "none",
    border: "none",
    padding: 0,
    fontSize: 13,
    color: "var(--text-mute)",
    cursor: "pointer",
    marginBottom: 14,
    display: "block",
  },
  pageTitle: {
    fontFamily: "var(--serif)",
    fontSize: 26,
    fontWeight: 400,
    color: "var(--text)",
    marginBottom: 6,
  },
  pageSubtitle: {
    fontSize: 14,
    color: "var(--text-soft)",
    lineHeight: 1.6,
    maxWidth: 560,
  },
  card: {
    background: "var(--surface)",
    border: "1px solid var(--border)",
    borderRadius: "var(--r)",
    padding: "24px",
    marginBottom: 32,
    display: "flex",
    flexDirection: "column",
    gap: 20,
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  label: {
    fontSize: 13,
    fontWeight: 500,
    color: "var(--text)",
    display: "flex",
    alignItems: "center",
    gap: 6,
  },
  required: {
    color: "var(--accent)",
    fontWeight: 400,
  },
  textarea: {
    width: "100%",
    padding: "10px 12px",
    fontSize: 13,
    fontFamily: "var(--sans)",
    color: "var(--text)",
    background: "var(--bg)",
    border: "1px solid var(--border)",
    borderRadius: "var(--r)",
    outline: "none",
    resize: "vertical",
    lineHeight: 1.6,
    transition: "border-color 0.15s",
    boxSizing: "border-box",
  },
  textareaWarn: {
    borderColor: "var(--accent)",
  },
  textareaFooter: {
    display: "flex",
    justifyContent: "flex-end",
  },
  wordCount: {
    fontSize: 11,
    transition: "color 0.2s",
  },
  error: {
    fontSize: 13,
    color: "var(--red)",
    background: "rgba(192,57,43,0.06)",
    border: "1px solid rgba(192,57,43,0.18)",
    borderRadius: "var(--r)",
    padding: "10px 14px",
  },
  actions: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    flexWrap: "wrap",
  },
  submitBtn: {
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    padding: "10px 22px",
    background: "var(--accent)",
    color: "#fff",
    border: "none",
    borderRadius: "var(--r)",
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
    transition: "background 0.15s",
    flexShrink: 0,
  },
  submitBtnDisabled: {
    background: "var(--border)",
    color: "var(--text-mute)",
    cursor: "not-allowed",
  },
  submitNote: {
    fontSize: 12,
    color: "var(--text-mute)",
    lineHeight: 1.5,
    maxWidth: 340,
  },
  spinner: {
    display: "inline-block",
    width: 12,
    height: 12,
    border: "2px solid rgba(255,255,255,0.3)",
    borderTopColor: "#fff",
    borderRadius: "50%",
    animation: "spin 0.7s linear infinite",
  },
};